import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Code, FormControl, FormHelperText,
  FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Table,
  TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack, useToast,
  List, ListItem,
} from '@chakra-ui/react';
import { FiCopy, FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import channexService from '../../services/channexService';
import i18n from '../../i18n';

const STATUS_COLORS = {
  confirmed: 'green', modified: 'blue', checked_in: 'purple',
  checked_out: 'gray', cancelled: 'red', pending: 'yellow',
};

// Translation keys per mode; translated where rendered.
const MODES = {
  replay: {
    label: 'channexLoggedBookings.modes.replay.label',
    color: 'blue',
    explain: 'channexLoggedBookings.modes.replay.explain',
    done: 'channexLoggedBookings.modes.replay.done',
    failed: 'channexLoggedBookings.modes.replay.failed',
    now: 'channexLoggedBookings.modes.replay.now',
    after: 'channexLoggedBookings.modes.replay.after',
  },
  recreate: {
    label: 'channexLoggedBookings.modes.recreate.label',
    color: 'red',
    explain: 'channexLoggedBookings.modes.recreate.explain',
    done: 'channexLoggedBookings.modes.recreate.done',
    failed: 'channexLoggedBookings.modes.recreate.failed',
    now: 'channexLoggedBookings.modes.recreate.now',
    after: 'channexLoggedBookings.modes.recreate.after',
  },
};

// navigator.clipboard needs a secure context; fall back to a hidden textarea.
const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(area);
  if (!ok) throw new Error(i18n.t('channexLoggedBookings.errors.clipboardUnavailable'));
};

const money = (value, currency) => (
  value === null || value === undefined || value === '' ? '—' : `${value}${currency ? ` ${currency}` : ''}`
);

// The snapshot the backend returns for before/after a replay.
const Snapshot = ({ title, snap, currency }) => {
  const { t } = useTranslation();
  return (
    <Box flex="1" borderWidth="1px" borderRadius="md" p={3}>
      <Text fontWeight="semibold" mb={1}>{title}</Text>
      {!snap ? <Text fontSize="sm" color="gray.500">{t('channexLoggedBookings.snapshot.noReservation')}</Text> : (
        <VStack align="start" spacing={0} fontSize="sm">
          <Text>{snap.reservation_id} · <Badge colorScheme={STATUS_COLORS[snap.status]}>{snap.status}</Badge></Text>
          <Text>{t('channexLoggedBookings.snapshot.roomsTotal', { amount: money(snap.rooms_total, currency) })}</Text>
          <Text>{t('channexLoggedBookings.labels.commission', { amount: money(snap.commission, currency) })}</Text>
          <Text>{t('channexLoggedBookings.snapshot.folioCharges', { amount: money(snap.folio_total_charges, currency) })}</Text>
          {(snap.payments || []).map((p) => (
            <Text key={p.reference} color="gray.600">
              {t('channexLoggedBookings.snapshot.payment', { reference: p.reference, amount: p.amount })}
            </Text>
          ))}
          {snap.assigned_rooms?.length > 0 && (
            <Text color="gray.600">{t('channexLoggedBookings.snapshot.roomsAssigned', { n: snap.assigned_rooms.length })}</Text>
          )}
        </VStack>
      )}
    </Box>
  );
};

/**
 * Pick a booking Channex already sent us and replay it.
 *
 * Acked revisions never come back on the Channex feed (and GET /bookings is
 * forbidden by certification), so the source is our own feed log. Every
 * action runs a dry run first and shows the before/after; nothing changes
 * until the second confirmation.
 */
const LoggedBookingsModal = ({ isOpen, onClose, config }) => {
  const [rows, setRows] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // {row, mode, result}
  // Import options: the room type to import an unmapped booking as, and
  // the physical room to put the booking in (both optional otherwise).
  const [roomTypeId, setRoomTypeId] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState('');
  const toast = useToast();
  const { t } = useTranslation();

  const load = useCallback(async () => {
    if (!config) return;
    setRows(null);
    setError(null);
    try {
      const data = await channexService.loggedBookings(config.id, 200);
      if (data.error) setError(data.error);
      setRows(data.bookings || []);
      setRoomTypes(data.room_types || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRows([]);
    }
  }, [config]);

  useEffect(() => {
    if (isOpen) { setPreview(null); load(); }
  }, [isOpen, load]);

  const copyJson = async (row) => {
    try {
      await copyText(JSON.stringify(row.raw ?? row, null, 2));
      toast({ title: t('channexLoggedBookings.toasts.jsonCopied'), description: `${row.ota_name || ''} ${row.ota_code}`.trim(), status: 'success', duration: 2500 });
    } catch (err) {
      toast({ title: t('channexLoggedBookings.toasts.copyFailed'), description: err.message, status: 'error' });
    }
  };

  // A 400 still carries the dry-run body (blockers etc.), so surface it.
  const call = async (row, mode, dryRun, options = {}) => {
    try {
      return await channexService.replayBooking(config.id, row.ota_code, { dryRun, mode, ...options });
    } catch (err) {
      if (err.response?.data && typeof err.response.data === 'object') return err.response.data;
      throw err;
    }
  };

  const startPreview = async (row, mode, options = {}) => {
    setBusy(`${row.ota_code}:${mode}`);
    try {
      const result = await call(row, mode, true, options);
      setPreview({ row, mode, result });
    } catch (err) {
      toast({ title: t('channexLoggedBookings.toasts.previewFailed'), description: err.message, status: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const openPreview = (row, mode) => {
    setRoomTypeId('');
    setRoomNumber('');
    startPreview(row, mode);
  };

  const importOptions = () => ({ roomTypeId: roomTypeId || null, roomNumber: roomNumber.trim() });

  const apply = async () => {
    const { row, mode } = preview;
    setBusy('apply');
    try {
      const result = await call(row, mode, false, importOptions());
      if (result.success) {
        toast({
          title: t(MODES[mode].done),
          description: result.room_number
            ? t('channexLoggedBookings.toasts.appliedWithRoom', {
              code: row.ota_code,
              reservation: result.after?.reservation_id || '—',
              commission: money(result.after?.commission),
              room: result.room_number,
            })
            : t('channexLoggedBookings.toasts.applied', {
              code: row.ota_code,
              reservation: result.after?.reservation_id || '—',
              commission: money(result.after?.commission),
            }),
          status: 'success', duration: 8000,
        });
        setPreview(null);
        load();
      } else {
        setPreview({ row, mode, result });
        toast({ title: t(MODES[mode].failed), description: result.error, status: 'error', duration: 10000 });
      }
    } catch (err) {
      toast({ title: t(MODES[mode].failed), description: err.message, status: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const needle = search.trim().toLowerCase();
  const visibleRows = (rows || []).filter((row) => !needle
    || String(row.ota_code || '').toLowerCase().includes(needle)
    || String(row.unique_id || '').toLowerCase().includes(needle)
    || String(row.guest || '').toLowerCase().includes(needle));

  const renderPreview = () => {
    const { row, mode, result } = preview;
    const blocked = (result.blockers || []).length > 0;
    return (
      <VStack align="stretch" spacing={4}>
        <Alert status={mode === 'recreate' ? 'warning' : 'info'}>
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">
              {t('channexLoggedBookings.preview.heading', {
                action: t(MODES[mode].label), ota: row.ota_name, code: row.ota_code, guest: row.guest,
              })}
            </Text>
            <Text fontSize="sm">{t(MODES[mode].explain)}</Text>
          </Box>
        </Alert>
        {result.success === false && !blocked && (
          <Alert status="error"><AlertIcon />{result.error}</Alert>
        )}
        <Box borderWidth="1px" borderRadius="md" p={3}>
          <Text fontWeight="semibold" mb={2}>{t('channexLoggedBookings.preview.importOptions')}</Text>
          <HStack align="end" spacing={3} flexWrap="wrap">
            {(row.unmapped_rooms || result.unmapped_rooms) && (
              <FormControl maxW="320px" isRequired>
                <FormLabel fontSize="sm" mb={1}>{t('channexLoggedBookings.preview.importAsRoomType')}</FormLabel>
                <Select size="sm" placeholder={t('channexLoggedBookings.preview.chooseRoomType')}
                  value={roomTypeId} onChange={(e) => setRoomTypeId(e.target.value)}>
                  {roomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                </Select>
                <FormHelperText fontSize="xs">
                  {t('channexLoggedBookings.preview.unmappedHelp')}
                </FormHelperText>
              </FormControl>
            )}
            <FormControl maxW="160px">
              <FormLabel fontSize="sm" mb={1}>{t('channexLoggedBookings.preview.putInRoom')}</FormLabel>
              <Input size="sm" placeholder={t('channexLoggedBookings.preview.roomPlaceholder')} value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)} />
              <FormHelperText fontSize="xs">{t('channexLoggedBookings.preview.roomHelp')}</FormHelperText>
            </FormControl>
            <Button size="sm" variant="outline"
              isLoading={busy === `${row.ota_code}:${mode}`}
              onClick={() => startPreview(row, mode, importOptions())}>
              {t('channexLoggedBookings.preview.updatePreview')}
            </Button>
          </HStack>
        </Box>
        {blocked && (
          <Alert status="error" alignItems="start">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">{t('channexLoggedBookings.preview.blockedTitle')}</Text>
              <List fontSize="sm">{result.blockers.map((b) => <ListItem key={b}>• {b}</ListItem>)}</List>
              <Text fontSize="sm" mt={1}>{t('channexLoggedBookings.preview.blockedHint')}</Text>
            </Box>
          </Alert>
        )}
        {result.parsed && (
          <HStack align="stretch" spacing={3}>
            <Snapshot title={t('channexLoggedBookings.preview.now')} snap={result.before} currency={row.currency} />
            <Box flex="1" borderWidth="1px" borderRadius="md" p={3} bg="blue.50" _dark={{ bg: 'blue.900' }}>
              <Text fontWeight="semibold" mb={1}>{t(MODES[mode].after)}</Text>
              <VStack align="start" spacing={0} fontSize="sm">
                {mode === 'recreate' && result.before && <Text>{t('channexLoggedBookings.preview.newReservation')}</Text>}
                <Text>{t('channexLoggedBookings.preview.bookingTotal', { amount: money(result.parsed.total_amount, row.currency) })}</Text>
                <Text>{t('channexLoggedBookings.labels.commission', { amount: money(result.parsed.commission, row.currency) })}</Text>
                <Text>{t('channexLoggedBookings.preview.roomLines', { lines: (result.parsed.room_amounts || []).join(' + ') || '—' })}</Text>
                {result.mapping_override && (
                  <Text>{t('channexLoggedBookings.preview.roomTypeOverride', { name: result.mapping_override.room_type_name })}</Text>
                )}
                {result.room_number && <Text>{t('channexLoggedBookings.preview.room', { room: result.room_number })}</Text>}
              </VStack>
            </Box>
          </HStack>
        )}
      </VStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('channexLoggedBookings.title', { hotel: config?.hotel?.name })}
          <Text fontSize="sm" fontWeight="normal" color="gray.500">
            {t('channexLoggedBookings.subtitle')}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {preview ? renderPreview() : (
            <>
              {error && <Alert status="warning" mb={3}><AlertIcon />{error}</Alert>}
              <Input size="sm" mb={3} maxW="360px" placeholder={t('channexLoggedBookings.list.searchPlaceholder')}
                value={search} onChange={(e) => setSearch(e.target.value)} />
              {rows === null ? <HStack><Spinner size="sm" /><Text>{t('channexLoggedBookings.list.loading')}</Text></HStack> : visibleRows.length === 0 ? (
                <Text color="gray.500">{rows.length === 0 ? t('channexLoggedBookings.list.empty') : t('channexLoggedBookings.list.noMatch')}</Text>
              ) : (
                <TableContainer>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>{t('channexLoggedBookings.list.received')}</Th>
                        <Th>{t('channexLoggedBookings.list.otaCode')}</Th>
                        <Th>{t('channexLoggedBookings.list.guest')}</Th>
                        <Th>{t('channexLoggedBookings.list.stay')}</Th>
                        <Th isNumeric>{t('channexLoggedBookings.list.channexAmount')}</Th>
                        <Th>{t('channexLoggedBookings.list.fdReservation')}</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {visibleRows.map((row) => {
                        const res = row.reservation;
                        const cancelled = ['cancelled', 'canceled'].includes(row.revision_status);
                        const needsFix = res && row.net_payout && !res.commission;
                        return (
                          <Tr key={row.ota_code}>
                            <Td fontSize="xs" whiteSpace="nowrap">
                              {row.received_at ? new Date(`${row.received_at}Z`).toLocaleString() : '—'}
                            </Td>
                            <Td>
                              <Text fontSize="sm">{row.ota_name}</Text>
                              <Code fontSize="xs">{row.ota_code}</Code>{' '}
                              <Badge colorScheme={cancelled ? 'red' : 'gray'}>{row.revision_status}</Badge>
                              {row.revisions_logged > 1 && (
                                <Text fontSize="xs" color="gray.500">{t('channexLoggedBookings.list.revisions', { count: row.revisions_logged })}</Text>
                              )}
                              {row.unmapped_rooms && !cancelled && (
                                <Tooltip label={t('channexLoggedBookings.list.roomUnmappedTooltip')}>
                                  <Badge colorScheme="orange" ml={1}>{t('channexLoggedBookings.list.roomUnmapped')}</Badge>
                                </Tooltip>
                              )}
                            </Td>
                            <Td fontSize="sm">{row.guest || '—'}</Td>
                            <Td fontSize="xs" whiteSpace="nowrap">{row.arrival_date} → {row.departure_date}</Td>
                            <Td isNumeric fontSize="sm">
                              {money(row.amount, row.currency)}
                              <Text fontSize="xs" color="gray.500">
                                {row.ota_commission
                                  ? t('channexLoggedBookings.list.commissionValue', { amount: row.ota_commission })
                                  : row.net_payout
                                    ? t('channexLoggedBookings.list.netPayout')
                                    : t('channexLoggedBookings.list.noCommission')}
                              </Text>
                            </Td>
                            <Td fontSize="sm">
                              {res ? (
                                <>
                                  <Text>{res.reservation_id} <Badge colorScheme={STATUS_COLORS[res.status]}>{res.status}</Badge></Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {t('channexLoggedBookings.list.reservationTotals', {
                                      total: res.rooms_total, commission: res.commission || '—',
                                    })}
                                  </Text>
                                  {needsFix && <Badge colorScheme="orange">{t('channexLoggedBookings.list.commissionMissing')}</Badge>}
                                </>
                              ) : (
                                <Badge colorScheme={cancelled ? 'gray' : 'red'}>{cancelled ? t('channexLoggedBookings.list.notImported') : t('channexLoggedBookings.list.missingInFd')}</Badge>
                              )}
                            </Td>
                            <Td>
                              <HStack spacing={1} justify="end">
                                <Tooltip label={t('channexLoggedBookings.list.copyJsonTooltip')}>
                                  <Button size="xs" variant="ghost" leftIcon={<FiCopy />}
                                    onClick={() => copyJson(row)}>
                                    JSON
                                  </Button>
                                </Tooltip>
                                <Tooltip label={res ? t('channexLoggedBookings.list.replayTooltip') : t('channexLoggedBookings.list.importTooltip')}>
                                  <Button size="xs" colorScheme="blue" variant="outline"
                                    isDisabled={cancelled}
                                    isLoading={busy === `${row.ota_code}:replay`}
                                    onClick={() => openPreview(row, 'replay')}>
                                    {res ? t(MODES.replay.label) : t('channexLoggedBookings.list.import')}
                                  </Button>
                                </Tooltip>
                                {res && res.status !== 'cancelled' && (
                                  <Button size="xs" colorScheme="red" variant="outline"
                                    isDisabled={cancelled}
                                    isLoading={busy === `${row.ota_code}:recreate`}
                                    onClick={() => openPreview(row, 'recreate')}>
                                    {t(MODES.recreate.label)}
                                  </Button>
                                )}
                              </HStack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {preview ? (
            <HStack>
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>{t('channexLoggedBookings.buttons.backToList')}</Button>
              <Button size="sm" colorScheme={MODES[preview.mode].color}
                isDisabled={!preview.result.success}
                isLoading={busy === 'apply'}
                onClick={apply}>
                {t(MODES[preview.mode].now)}
              </Button>
            </HStack>
          ) : (
            <HStack>
              <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={load}>{t('channexLoggedBookings.buttons.refresh')}</Button>
              <Button size="sm" onClick={onClose}>{t('channexLoggedBookings.buttons.close')}</Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LoggedBookingsModal;
